/**
 * App-wide context for the /api/info endpoint.
 *
 * The `/api/info` response carries environment-level configuration that many
 * parts of the app need before (and after) authentication: license, CAPCO
 * banner, password policy, auth tab configuration, build metadata, feature
 * functionals, scheduler defaults, etc.
 *
 * This provider fetches it once on hard refresh / app load and exposes it via
 * the `useAppInfo()` hook (`./useAppInfo`). Mount it *outside* AuthProvider in
 * main.tsx so public pages (login, reset) can read info without an auth
 * session. A signed-in page waits for it (ProtectedRoute, #2399); if it
 * failed, that page's panel calls `refreshInfo()` until it loads.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { isAxiosError } from 'axios';
import { fetchSystemInfo } from '@/api/auth';
import type { SystemInfo } from '@/types';
import { AppInfoContext, type AppInfoContextType } from './appInfoContextObject';

/** A 401: the API's auth ladder (silent refresh, then the re-auth prompt) already had its turn. */
function isUnauthorized(err: unknown): boolean {
  return isAxiosError(err) && err.response?.status === 401;
}

export function AppInfoProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const [info, setInfo] = useState<SystemInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [unauthorized, setUnauthorized] = useState(false);

  const refreshInfo = useCallback(async () => {
    try {
      const next = await fetchSystemInfo();
      setInfo(next);
      setUnauthorized(false);
    } catch (err) {
      console.error('Failed to fetch /api/info:', err);
      setUnauthorized(isUnauthorized(err));
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    // fire-and-forget: the IIFE catches and logs
    void (async () => {
      try {
        const next = await fetchSystemInfo();
        if (!cancelled) setInfo(next);
      } catch (err) {
        console.error('Failed to fetch /api/info:', err);
        if (!cancelled) setUnauthorized(isUnauthorized(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo<AppInfoContextType>(
    () => ({ info, loading, unauthorized, refreshInfo }),
    [info, loading, unauthorized, refreshInfo],
  );

  return <AppInfoContext.Provider value={value}>{children}</AppInfoContext.Provider>;
}
